import * as THREE from "three";
import { LANE_X, LaneIndex, clampLane } from "./Lanes";

const LANE_SPRING_FREQ = 14; // Hz
const LANE_SPRING_DAMP = 1.0; // critical damping

function springStep(
  x: number,
  v: number,
  target: number,
  dt: number,
  freqHz: number,
  damp: number,
): [number, number] {
  const omega = 2 * Math.PI * freqHz;
  const f = 1 + 2 * dt * damp * omega;
  const oo = omega * omega;
  const hoo = dt * oo;
  const hhoo = dt * hoo;
  const detInv = 1 / (f + hhoo);
  const detX = (f * x + dt * v + hhoo * target) * detInv;
  const detV = (v + hoo * (target - x)) * detInv;
  return [detX, detV];
}

export class PlayerCar {
  readonly group: THREE.Group;
  readonly body: THREE.Mesh;

  private lane: LaneIndex = 1;
  private targetX = 0;
  private currentX = 0;
  private velX = 0;

  private readonly baseY = 0.55;

  constructor() {
    this.group = new THREE.Group();

    const bodyMat = new THREE.MeshStandardMaterial({
      color: 0xff2a55,
      roughness: 0.35,
      metalness: 0.7,
    });
    const cabinMat = new THREE.MeshStandardMaterial({
      color: 0x111219,
      roughness: 0.25,
      metalness: 0.4,
    });
    const wheelMat = new THREE.MeshStandardMaterial({
      color: 0x111111,
      roughness: 0.9,
      metalness: 0.1,
    });

    const body = new THREE.Mesh(new THREE.BoxGeometry(1.2, 0.45, 2.4), bodyMat);
    body.castShadow = true;
    body.position.y = 0.35;
    this.body = body;

    const cabin = new THREE.Mesh(new THREE.BoxGeometry(1.0, 0.35, 1.1), cabinMat);
    cabin.position.y = 0.7;
    cabin.position.z = -0.05;

    const wheelGeo = new THREE.CylinderGeometry(0.22, 0.22, 0.25, 16);
    wheelGeo.rotateZ(Math.PI / 2);
    for (const [x, z] of [[0.55, 0.85], [-0.55, 0.85], [0.55, -0.85], [-0.55, -0.85]] as [number, number][]) {
      const w = new THREE.Mesh(wheelGeo, wheelMat);
      w.position.set(x, 0.22, z);
      this.group.add(w);
    }

    const tailMat = new THREE.MeshStandardMaterial({
      color: 0xff3344, emissive: 0xff2233, emissiveIntensity: 1.4,
    });
    const tail1 = new THREE.Mesh(new THREE.BoxGeometry(0.32, 0.08, 0.04), tailMat);
    tail1.position.set(0.32, 0.45, 1.2);
    const tail2 = tail1.clone();
    tail2.position.x = -0.32;

    const headMat = new THREE.MeshStandardMaterial({
      color: 0xfff4d6, emissive: 0xfff4d6, emissiveIntensity: 1.6,
    });
    const head1 = new THREE.Mesh(new THREE.BoxGeometry(0.3, 0.08, 0.04), headMat);
    head1.position.set(0.34, 0.45, -1.2);
    const head2 = head1.clone();
    head2.position.x = -0.34;

    this.group.add(body, cabin, tail1, tail2, head1, head2);
    this.group.position.set(0, this.baseY, 0);
  }

  reset(): void {
    this.lane = 1;
    this.targetX = LANE_X[1];
    this.currentX = LANE_X[1];
    this.velX = 0;
    this.group.position.set(this.currentX, this.baseY, 0);
    this.group.rotation.z = 0;
  }

  swipeLeft(): void {
    this.lane = clampLane(this.lane - 1) as LaneIndex;
    this.targetX = LANE_X[this.lane];
  }
  swipeRight(): void {
    this.lane = clampLane(this.lane + 1) as LaneIndex;
    this.targetX = LANE_X[this.lane];
  }

  laneIndex(): LaneIndex { return this.lane; }

  hitbox(out: THREE.Box3): THREE.Box3 {
    const x = this.group.position.x;
    const y = this.group.position.y;
    out.min.set(x - 0.55, y - 0.4, -1.05);
    out.max.set(x + 0.55, y + 0.4,  1.05);
    return out;
  }

  update(dt: number): void {
    const [nx, nv] = springStep(
      this.currentX, this.velX, this.targetX,
      dt, LANE_SPRING_FREQ, LANE_SPRING_DAMP,
    );
    this.currentX = nx;
    this.velX = nv;

    this.group.position.x = this.currentX;
    this.group.position.y = this.baseY;

    // Tilt into the turn
    const tiltMax = 0.18;
    const target = -((this.targetX - this.currentX) / 2.6) * tiltMax * 4;
    this.group.rotation.z += (target - this.group.rotation.z) * Math.min(1, dt * 12);
  }
}
